import {notifyRoamingSettingsChanged} from './roamingSettingsSync';

export const LOXO_ROAMING_KEYS = {
  emailSyncOnSend: 'emailSyncOnSend',
  jwt: 'jwt',
} as const;

const LOXO_AUTH_STORAGE_KEY = 'loxo-outlook-auth';

type StoredAuth = {
  podUrl?: string;
  refreshToken?: string;
  emailSyncOnSend?: boolean;
  updatedAt?: number;
};

export const getRoamingSettings = (): Office.RoamingSettings | null => {
  if (
    typeof Office === 'undefined' ||
    Office.context?.roamingSettings == null
  ) {
    return null;
  }
  return Office.context.roamingSettings;
};

export const saveRoamingSettingsAsync = (): Promise<void> => {
  const settings = getRoamingSettings();
  if (!settings) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    settings.saveAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Failed) {
        reject(new Error(result.error.message));
        return;
      }
      resolve();
    });
  });
};

export const getEmailSyncOnSend = (): boolean | undefined => {
  const storedValue = readStoredAuth().emailSyncOnSend;
  if (typeof storedValue === 'boolean') {
    return storedValue;
  }

  const settings = getRoamingSettings();
  if (!settings) {
    return undefined;
  }
  return settings.get(LOXO_ROAMING_KEYS.emailSyncOnSend);
};

const readStoredAuth = (): StoredAuth => {
  if (typeof localStorage === 'undefined') {
    return {};
  }

  const raw = localStorage.getItem(LOXO_AUTH_STORAGE_KEY);
  if (!raw) {
    return {};
  }

  try {
    const parsed = JSON.parse(raw) as StoredAuth;
    if (!parsed || typeof parsed !== 'object') {
      return {};
    }
    return {
      podUrl: typeof parsed.podUrl === 'string' ? parsed.podUrl : undefined,
      refreshToken:
        typeof parsed.refreshToken === 'string'
          ? parsed.refreshToken
          : undefined,
      emailSyncOnSend:
        typeof parsed.emailSyncOnSend === 'boolean'
          ? parsed.emailSyncOnSend
          : undefined,
      updatedAt:
        typeof parsed.updatedAt === 'number' ? parsed.updatedAt : undefined,
    };
  } catch {
    return {};
  }
};

const writeStoredAuth = (auth: StoredAuth): void => {
  if (typeof localStorage === 'undefined') {
    throw new Error('Local storage is not available in this environment.');
  }

  localStorage.setItem(
    LOXO_AUTH_STORAGE_KEY,
    JSON.stringify({...auth, updatedAt: Date.now()})
  );
  notifyRoamingSettingsChanged();
};

export const getPodUrl = (): string | undefined => {
  return readStoredAuth().podUrl;
};

export const getRefreshToken = (): string | undefined => {
  return readStoredAuth().refreshToken;
};

export const getJwt = (): string | undefined => {
  const settings = getRoamingSettings();
  if (!settings) {
    return undefined;
  }
  const value = settings.get(LOXO_ROAMING_KEYS.jwt);
  return value ? String(value) : undefined;
};

const setRoamingValue = async (key: string, value: unknown): Promise<void> => {
  const settings = getRoamingSettings();
  if (!settings) {
    throw new Error('Roaming settings are not available in this environment.');
  }
  const previousValue = settings.get(key);
  settings.set(key, value);
  try {
    await saveRoamingSettingsAsync();
  } catch (error) {
    if (previousValue === undefined) {
      settings.remove(key);
    } else {
      settings.set(key, previousValue);
    }
    throw error;
  }
  notifyRoamingSettingsChanged();
};

export const setEmailSyncOnSend = async (value: boolean): Promise<void> => {
  try {
    writeStoredAuth({...readStoredAuth(), emailSyncOnSend: value});
  } catch {
    await setRoamingValue(LOXO_ROAMING_KEYS.emailSyncOnSend, value);
    return;
  }

  const settings = getRoamingSettings();
  if (!settings) {
    return;
  }

  const previousValue = settings.get(LOXO_ROAMING_KEYS.emailSyncOnSend);
  settings.set(LOXO_ROAMING_KEYS.emailSyncOnSend, value);
  try {
    await saveRoamingSettingsAsync();
  } catch {
    if (previousValue === undefined) {
      settings.remove(LOXO_ROAMING_KEYS.emailSyncOnSend);
    } else {
      settings.set(LOXO_ROAMING_KEYS.emailSyncOnSend, previousValue);
    }
  }
};

export const setPodUrl = async (podUrl: string): Promise<void> => {
  writeStoredAuth({...readStoredAuth(), podUrl});
};

export const setRefreshToken = async (refreshToken: string): Promise<void> => {
  writeStoredAuth({...readStoredAuth(), refreshToken});
};

export const setJwt = async (jwt: string): Promise<void> => {
  await setRoamingValue(LOXO_ROAMING_KEYS.jwt, jwt);
};

export const clearRefreshToken = async (): Promise<void> => {
  const auth = readStoredAuth();
  delete auth.refreshToken;
  writeStoredAuth(auth);
};

export const saveAuthToRoaming = async (params: {
  podUrl: string;
  jwt: string;
  refreshToken: string;
}): Promise<void> => {
  const settings = getRoamingSettings();
  const previousJwt = settings?.get(LOXO_ROAMING_KEYS.jwt);
  writeStoredAuth({
    ...readStoredAuth(),
    podUrl: params.podUrl,
    refreshToken: params.refreshToken,
  });
  settings?.remove(LOXO_ROAMING_KEYS.jwt);
  try {
    await saveRoamingSettingsAsync();
  } catch {
    if (!settings) {
      return;
    }
    if (previousJwt === undefined) {
      settings.remove(LOXO_ROAMING_KEYS.jwt);
    } else {
      settings.set(LOXO_ROAMING_KEYS.jwt, previousJwt);
    }
  }
  notifyRoamingSettingsChanged();
};

export const clearJwtAndRefreshTokens = async (): Promise<void> => {
  const settings = getRoamingSettings();
  const previousJwt = settings?.get(LOXO_ROAMING_KEYS.jwt);
  const auth = readStoredAuth();
  delete auth.refreshToken;
  writeStoredAuth(auth);
  settings?.remove(LOXO_ROAMING_KEYS.jwt);
  try {
    await saveRoamingSettingsAsync();
  } catch {
    if (settings && previousJwt !== undefined) {
      settings.set(LOXO_ROAMING_KEYS.jwt, previousJwt);
    }
  }
  notifyRoamingSettingsChanged();
};
